/**
 * Phase 4 — Vendor Invoices tab (Stores page).
 * Register incoming vendor invoices, run the 3-way match (PO/WO ↔ GRN/WPC ↔
 * invoice, net of open debit notes), finance approval and payment recording.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Receipt, Plus, Loader2, Search, CheckCircle2, Banknote, Scale } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';

const STATUS_BADGE = {
  pending: 'bg-slate-100 text-slate-600',
  matched: 'bg-teal-50 text-teal-700',
  mismatch: 'bg-red-50 text-red-700',
  approved: 'bg-emerald-50 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-800',
};
const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#1F7A6E]';

export function VendorInvoicesTab() {
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState(null);
  const [fStatus, setFStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (fStatus) params.match_status = fStatus;
      if (q) params.q = q;
      const [d, dd] = await Promise.all([
        apiGet('/vendor-invoices', params),
        apiGet('/vendor-invoices/dashboard'),
      ]);
      setItems(d?.items || []);
      setDash(dd);
    } catch { toast.error('Failed to load vendor invoices'); }
    finally { setLoading(false); }
  }, [fStatus, q]);
  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (inv, path, body) => {
    setBusyId(inv.id);
    try {
      await apiPost(`/vendor-invoices/${inv.id}/${path}`, body || {});
      toast.success('Done');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="vendor-invoices-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="kpi-overline flex items-center gap-1.5"><Receipt size={11} /> Accounts Payable</div>
          <h3 className="font-display font-bold text-base mt-1">Vendor Invoices · 3-Way Match</h3>
        </div>
        <button onClick={() => setRegisterOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-bold uppercase tracking-wider"
          data-testid="vi-register-btn">
          <Plus size={12} /> Register Invoice
        </button>
      </div>

      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Pending Match" value={dash.total_pending_match} testid="vi-kpi-pending" />
          <Kpi label="Approved · Unpaid" value={`${dash.total_approved_unpaid} (${formatINR(dash.approved_unpaid_value)})`} testid="vi-kpi-approved" />
          <Kpi label="Overdue" value={dash.overdue_count} testid="vi-kpi-overdue" />
          <Kpi label="Total AP Value" value={formatINR(dash.total_ap_value)} testid="vi-kpi-ap" />
        </div>
      )}

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice no, vendor…"
            className={`${inp} pl-7 py-2`} data-testid="vi-search" />
        </div>
        {['', 'pending', 'matched', 'mismatch', 'approved', 'paid'].map((s) => (
          <button key={s || 'all'} onClick={() => setFStatus(s)}
            className={`px-2.5 py-1.5 text-[11px] rounded-sm font-display font-bold uppercase ${fStatus === s ? 'bg-[#1F7A6E] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'}`}
            data-testid={`vi-filter-${s || 'all'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" /></div> : (
          <table className="w-full dense-table" data-testid="vi-table">
            <thead><tr><th>Invoice No</th><th>Vendor</th><th>Linked Doc</th><th className="text-right">Gross</th><th className="text-right">Net Payable</th><th>Match</th><th>Due</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-slate-400 text-xs uppercase tracking-widest font-display">No vendor invoices registered</td></tr>}
              {items.map((inv) => (
                <React.Fragment key={inv.id}>
                  <tr className="cursor-pointer" onClick={() => setExpanded(expanded === inv.id ? null : inv.id)} data-testid={`vi-row-${inv.invoice_no}`}>
                    <td className="font-mono text-xs">{inv.invoice_no}</td>
                    <td className="text-xs">{inv.vendor_name}</td>
                    <td className="text-xs uppercase">{inv.linked_doc_type === 'work_order' ? 'WO' : inv.linked_doc_type} · <span className="font-mono">{inv.linked_doc_no || (inv.linked_doc_id || '').slice(0, 8)}</span></td>
                    <td className="text-right tabular-nums text-xs">{formatINR(inv.gross_amount)}</td>
                    <td className="text-right tabular-nums text-xs font-display font-bold">{formatINR(inv.net_payable)}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${STATUS_BADGE[inv.match_status] || ''}`} data-testid={`vi-status-${inv.invoice_no}`}>{inv.match_status}</span></td>
                    <td className="text-xs text-slate-500">{inv.payment_due_date ? formatDate(inv.payment_due_date) : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        {['pending', 'mismatch', 'matched'].includes(inv.match_status) && (
                          <button onClick={() => act(inv, 'match')} disabled={busyId === inv.id}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-[#1F7A6E] text-white rounded-sm font-display font-bold disabled:opacity-50"
                            data-testid={`vi-match-${inv.invoice_no}`}>
                            <Scale size={10} /> Run Match
                          </button>
                        )}
                        {inv.match_status === 'matched' && (
                          <button onClick={() => act(inv, 'approve')} disabled={busyId === inv.id}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50"
                            data-testid={`vi-approve-${inv.invoice_no}`}>
                            <CheckCircle2 size={10} /> Approve
                          </button>
                        )}
                        {inv.match_status === 'approved' && (
                          <button onClick={() => {
                            const ref = window.prompt('Payment reference (NEFT/UTR):') || '';
                            act(inv, 'mark-paid', { payment_ref: ref });
                          }} disabled={busyId === inv.id}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-emerald-700 text-white rounded-sm font-display font-bold disabled:opacity-50"
                            data-testid={`vi-paid-${inv.invoice_no}`}>
                            <Banknote size={10} /> Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded === inv.id && (
                    <tr>
                      <td colSpan="8" className="bg-slate-50 px-4 py-3" data-testid={`vi-detail-${inv.invoice_no}`}>
                        {inv.match_breakdown ? (
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
                            <Mini label={inv.linked_doc_type === 'work_order' ? 'WPC Certified' : 'GRN Value'} value={formatINR(inv.match_breakdown.grn_value ?? inv.match_breakdown.wpc_net_payable)} />
                            <Mini label="Open Debit Notes" value={`− ${formatINR(inv.match_breakdown.debit_notes_total)}`} />
                            <Mini label="Expected" value={formatINR(inv.match_breakdown.expected)} />
                            <Mini label="Invoice" value={formatINR(inv.match_breakdown.invoice_amount)} />
                            <Mini label="Difference" value={formatINR(inv.match_breakdown.difference)} red={Math.abs(inv.match_breakdown.difference || 0) > (inv.match_breakdown.tolerance || 1)} />
                            {inv.mismatch_reason && <div className="col-span-full text-red-600">{inv.mismatch_reason}</div>}
                            {(inv.debit_notes_adjusted || []).length > 0 && (
                              <div className="col-span-full text-slate-600">
                                Debit notes adjusted: {inv.debit_notes_adjusted.map((d) => `${d.dn_no} (${formatINR(d.amount)})`).join(', ')}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-xs text-slate-400">Match not run yet.</span>}
                        {inv.match_status === 'paid' && (
                          <div className="text-xs text-emerald-700 mt-2">Paid {formatINR(inv.paid_amount)} on {formatDate(inv.paid_on)} · Ref {inv.payment_ref || '—'}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {registerOpen && <RegisterInvoiceSheet onClose={(saved) => { setRegisterOpen(false); if (saved) load(); }} />}
    </div>
  );
}

const Kpi = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm px-3 py-2.5" data-testid={testid}>
    <div className="kpi-overline">{label}</div>
    <div className="font-display font-bold text-base text-slate-900 mt-0.5">{value}</div>
  </div>
);

const Mini = ({ label, value, red }) => (
  <div>
    <div className="kpi-overline">{label}</div>
    <div className={`font-display font-bold mt-0.5 ${red ? 'text-red-600' : 'text-slate-900'}`}>{value}</div>
  </div>
);

function RegisterInvoiceSheet({ onClose }) {
  const [f, setF] = useState({
    invoice_no: '', invoice_date: '', vendor_id: '', vendor_name: '',
    linked_doc_type: 'po', linked_doc_id: '', linked_doc_no: '',
    gross_amount: '', gst_amount: '', tds_amount: '', payment_due_date: '',
  });
  const [vendorQ, setVendorQ] = useState('');
  const [vendorResults, setVendorResults] = useState([]);
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);

  // load linkable docs when type or vendor changes
  useEffect(() => {
    const loadDocs = async () => {
      try {
        if (f.linked_doc_type === 'po') {
          const pp = await apiGet('/po', { limit: 100 });
          let all = (pp?.items || []).map((p) => ({ id: p.id, no: p.po_no, label: `${p.po_no} · ${p.vendor_name || ''}`, vendor_id: p.vendor_id }));
          if (f.vendor_id) all = all.filter((d) => !d.vendor_id || d.vendor_id === f.vendor_id);
          setDocs(all.slice(0, 100));
        } else {
          const d = await apiGet('/work-orders', f.vendor_id ? { vendor_id: f.vendor_id, limit: 100 } : { limit: 100 });
          setDocs((d?.items || []).map((w) => ({ id: w.id, no: w.wo_no, label: `${w.wo_no} · ${w.title}` })));
        }
      } catch { setDocs([]); }
    };
    loadDocs();
  }, [f.linked_doc_type, f.vendor_id]);

  const searchVendors = (text) => {
    setVendorQ(text);
    if (!text) { setVendorResults([]); return; }
    apiGet('/vendors/search', { q: text }).then((rows) => setVendorResults(rows || [])).catch(() => {});
  };

  const save = async () => {
    if (!f.invoice_no || !f.vendor_id || !f.linked_doc_id || !Number(f.gross_amount)) {
      toast.error('Invoice no, vendor, linked document and gross amount are required'); return;
    }
    setSaving(true);
    try {
      await apiPost('/vendor-invoices', {
        ...f,
        gross_amount: Number(f.gross_amount), gst_amount: Number(f.gst_amount) || 0,
        tds_amount: Number(f.tds_amount) || 0,
      });
      toast.success('Invoice registered');
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to register'); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose(false)}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="vi-register-sheet">
        <SheetHeader>
          <SheetTitle className="font-display">Register Vendor Invoice</SheetTitle>
          <SheetDescription>Link the incoming invoice to its PO or Work Order — the 3-way match runs after registration.</SheetDescription>
        </SheetHeader>
        <div className="space-y-3 mt-4">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] text-slate-500 font-display uppercase">Invoice no *
              <input value={f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} className={`${inp} mt-1`} data-testid="vi-form-no" /></label>
            <label className="text-[10px] text-slate-500 font-display uppercase">Invoice date
              <input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} className={`${inp} mt-1`} /></label>
          </div>

          {/* Vendor typeahead */}
          {f.vendor_id ? (
            <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded-sm px-2.5 py-2">
              <span className="text-xs font-display font-bold">{f.vendor_name}</span>
              <button onClick={() => setF({ ...f, vendor_id: '', vendor_name: '' })} className="text-[11px] text-slate-500 hover:text-red-600">Change</button>
            </div>
          ) : (
            <div className="relative">
              <label className="text-[10px] text-slate-500 font-display uppercase">Vendor *</label>
              <input value={vendorQ} onChange={(e) => searchVendors(e.target.value)} placeholder="Type to search vendor master…" className={`${inp} mt-1`} data-testid="vi-form-vendor-search" />
              {vendorResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-40 overflow-y-auto">
                  {vendorResults.map((v) => (
                    <button key={v.id} onClick={() => { setF({ ...f, vendor_id: v.id, vendor_name: v.name }); setVendorResults([]); setVendorQ(''); }}
                      className="w-full text-left px-2.5 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      data-testid={`vi-vendor-option-${v.vendor_code}`}>
                      <span className="font-mono text-slate-500">{v.vendor_code}</span> {v.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] text-slate-500 font-display uppercase">Linked to *
              <select value={f.linked_doc_type} onChange={(e) => setF({ ...f, linked_doc_type: e.target.value, linked_doc_id: '', linked_doc_no: '' })} className={`${inp} mt-1`} data-testid="vi-form-doctype">
                <option value="po">Purchase Order</option>
                <option value="work_order">Work Order</option>
              </select></label>
            <label className="text-[10px] text-slate-500 font-display uppercase">Document *
              <select value={f.linked_doc_id} onChange={(e) => {
                const d = docs.find((x) => x.id === e.target.value);
                setF({ ...f, linked_doc_id: e.target.value, linked_doc_no: d?.no || '' });
              }} className={`${inp} mt-1`} data-testid="vi-form-doc">
                <option value="">— Select —</option>
                {docs.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select></label>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <label className="text-[10px] text-slate-500 font-display uppercase">Gross ₹ *
              <input type="number" value={f.gross_amount} onChange={(e) => setF({ ...f, gross_amount: e.target.value })} className={`${inp} mt-1`} data-testid="vi-form-gross" /></label>
            <label className="text-[10px] text-slate-500 font-display uppercase">GST ₹
              <input type="number" value={f.gst_amount} onChange={(e) => setF({ ...f, gst_amount: e.target.value })} className={`${inp} mt-1`} /></label>
            <label className="text-[10px] text-slate-500 font-display uppercase">TDS ₹
              <input type="number" value={f.tds_amount} onChange={(e) => setF({ ...f, tds_amount: e.target.value })} className={`${inp} mt-1`} /></label>
          </div>
          <label className="text-[10px] text-slate-500 font-display uppercase block">Payment due date
            <input type="date" value={f.payment_due_date} onChange={(e) => setF({ ...f, payment_due_date: e.target.value })} className={`${inp} mt-1 max-w-[200px]`} /></label>

          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={saving} className="px-4 py-2 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-50" data-testid="vi-form-save">
              {saving ? 'Saving…' : 'Register Invoice'}
            </button>
            <button onClick={() => onClose(false)} className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-sm font-display">Cancel</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
